import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React, { CSSProperties } from 'react';
import { areEqual } from 'react-window';
import styled, { css } from 'styled-components';

import { MenuContainer } from 'apis/restaurant-api/merchant-admin/menu2/types';
import MonetaryAmount from 'common/formatting/MonetaryAmount';

import TranslationInput from './TranslationInput';
import { TranslationLabelItem, TranslationSectionItem } from '../types';
import { buildSectionItemKey } from '../utils';

type TaskIdentifier = {
  key: string;
  pos: 'right' | 'left';
};

export type SectionsRendererData = {
  list: (TranslationLabelItem | TranslationSectionItem)[];
  menuData: MenuContainer;
  leftLanguage: Language | null;
  rightLanguage: Language | null;
  primaryLanguage: Language;
  erroredTasks: TaskIdentifier[];
  pendingTasks: TaskIdentifier[];
  onSaveTranslation: (data: { value: string; item: TranslationSectionItem; pos: 'right' | 'left' }) => void;
};

type Props = {
  index: number;
  data: SectionsRendererData;
  style: CSSProperties;
};

const Row = styled.div`
  display: flex;
`;

const LeftColumn = styled.div<{ overflowHidden?: boolean }>`
  flex: 1;
  margin: 0 24px 0 48px;
  ${({ overflowHidden }) =>
    overflowHidden &&
    css`
      overflow: hidden;
    `}
`;

const RightColumn = styled.div<{ overflowHidden?: boolean }>`
  flex: 1;
  margin: 0 48px 0 24px;
  ${({ overflowHidden }) =>
    overflowHidden &&
    css`
      overflow: hidden;
    `}
`;

const SectionLabel = styled.h2`
  height: 94px;
  font-weight: 700;
  font-size: 18px;
  color: ${cssThemedValue({ light: colors.pepper80, dark: colors.salt84 })};
  margin: 60px 0 13px;
  padding-inline-start: 1px;
`;

const ItemLabel = styled.label`
  height: 40px;
  display: block;
  font-size: 14px;
  color: ${cbSemanticCssVars.textPrimary};
  letter-spacing: -0.19px;
  margin-block-start: 24px;
  padding-inline-start: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SectionsRenderer = ({
  index,
  data: { list, menuData, leftLanguage, rightLanguage, primaryLanguage, erroredTasks, pendingTasks, onSaveTranslation },
  style,
}: Props) => {
  const rowData = list[index];

  if (rowData.itemType === 'section') {
    return (
      <Row style={style}>
        <LeftColumn overflowHidden>{leftLanguage && <SectionLabel>{rowData.label}</SectionLabel>}</LeftColumn>
        <RightColumn overflowHidden>{rightLanguage && <SectionLabel>{rowData.label}</SectionLabel>}</RightColumn>
      </Row>
    );
  }

  if (rowData.itemType === 'subsection') {
    return (
      <Row style={style}>
        <LeftColumn overflowHidden>
          {leftLanguage && (
            <ItemLabel>
              {rowData.label}
              {rowData.price !== null && (
                <>
                  {': '}
                  <MonetaryAmount amount={parseFloat(rowData.price.amount)} currency={rowData.price.currency} />
                </>
              )}
            </ItemLabel>
          )}
        </LeftColumn>
        <RightColumn overflowHidden>
          {rightLanguage && (
            <ItemLabel>
              {rowData.label}
              {rowData.price !== null && (
                <>
                  {': '}
                  <MonetaryAmount amount={parseFloat(rowData.price.amount)} currency={rowData.price.currency} />
                </>
              )}
            </ItemLabel>
          )}
        </RightColumn>
      </Row>
    );
  }

  const itemKey = buildSectionItemKey(rowData);
  const erroredLeftTask = erroredTasks.find(eItem => eItem.key === itemKey && eItem.pos === 'left');
  const erroredRightTask = erroredTasks.find(eItem => eItem.key === itemKey && eItem.pos === 'right');
  const pendingLeftTask = pendingTasks.find(eItem => eItem.key === itemKey && eItem.pos === 'left');
  const pendingRightTask = pendingTasks.find(eItem => eItem.key === itemKey && eItem.pos === 'right');

  return (
    <Row style={style}>
      <LeftColumn>
        {leftLanguage && (
          <TranslationInput
            menuData={menuData}
            language={leftLanguage}
            item={rowData}
            isRightSide={false}
            isError={!!erroredLeftTask}
            isPending={!!pendingLeftTask}
            primaryLanguage={primaryLanguage}
            onSaveTranslation={onSaveTranslation}
          />
        )}
      </LeftColumn>
      <RightColumn>
        {rightLanguage && (
          <TranslationInput
            menuData={menuData}
            language={rightLanguage}
            item={rowData}
            isRightSide
            isError={!!erroredRightTask}
            isPending={!!pendingRightTask}
            primaryLanguage={primaryLanguage}
            onSaveTranslation={onSaveTranslation}
          />
        )}
      </RightColumn>
    </Row>
  );
};

export default React.memo(SectionsRenderer, areEqual);
